import { promisify } from 'util';
import querystring from 'querystring';
import { WebAuth } from 'auth0-js';
import jwtDecode from 'jwt-decode';
import cookie from 'js-cookie';

const { prototype } = WebAuth;
['checkSession', 'parseHash'].forEach(k => {
  prototype[`${k}Async`] = promisify(prototype[k]);
});

const create = (config = {}) => {
  let timeout;

  const {
    auth: authConfig = {},
    token: {
      key = 'token',
      tolerance = 0,
      cookie: { domain, expires = 1000 * 60 * 60 * 24 * 365 * 2 } = {},
    } = {},
    logger,
  } = config;

  const { redirectUri } = authConfig;

  const auth = new WebAuth(authConfig);

  const log = val => {
    if (logger) {
      logger.info(val);
    }
  };

  const token = () =>
    cookie.get(key, {
      domain,
    });

  const removeToken = () => {
    cookie.remove(key, {
      domain,
    });
  };

  const login = ({ redirectUrl = redirectUri, ...options }) => {
    auth.authorize({
      state: JSON.stringify({
        redirectUrl,
      }),
      ...options,
    });
  };

  const logout = ({ returnTo } = {}) => {
    clearTimeout(timeout);
    removeToken();
    auth.logout({
      returnTo: returnTo || authConfig.returnTo || redirectUri,
    });
  };

  const expiresIn = accessToken => {
    const { exp } = jwtDecode(accessToken);
    return exp * 1000 - Date.now() - tolerance;
  };

  const set = accessToken =>
    cookie.set(key, accessToken, {
      expires: new Date(new Date().getTime() + expires),
      domain,
    });

  const parse = async (options = {}) => {
    const defaultOnRedirect = redirectUrl =>
      window.history.replaceState({}, '', redirectUrl);

    const { onRedirect = defaultOnRedirect } = options;

    const { hash } = window.location;
    if (/error/.test(hash)) {
      const err = querystring.parse(decodeURIComponent(hash.substring(1)));
      const error = new Error(err.error_description);
      throw Object.assign(err, error);
    }
    if (/access_token/.test(hash)) {
      const { accessToken, state } = await auth.parseHashAsync();
      set(accessToken);
      const { redirectUrl = '/' } = JSON.parse(state);
      await onRedirect(redirectUrl);
    }
  };

  const refresh = async (options = {}) => {
    const { ensureLoggedIn } = options;
    try {
      const { accessToken } = await auth.checkSessionAsync({});
      set(accessToken);
      log(accessToken);
    } catch (err) {
      clearTimeout(timeout);
      removeToken();
      if (ensureLoggedIn) {
        login({
          redirectUrl: window.location.pathname + window.location.search,
        });
      }
    }
  };

  const scheduleRefresh = (options = {}) => {
    const repeat = async () => {
      await refresh(options);
      scheduleRefresh(options);
    };
    const accessToken = token();
    if (accessToken) {
      timeout = setTimeout(repeat, expiresIn(accessToken));
    } else {
      clearTimeout(timeout);
    }
  };

  const init = async (options = {}) => {
    const { onRedirect, ensureLoggedIn = true, loginOptions } = options;

    try {
      await parse({
        onRedirect,
      });
    } catch (err) {
      removeToken();
      log(err);
    }

    const accessToken = token();

    if (accessToken) {
      log(accessToken);
      scheduleRefresh({ ensureLoggedIn });
      return accessToken;
    }

    if (ensureLoggedIn) {
      login({
        redirectUrl: window.location.pathname + window.location.search,
        ...loginOptions,
      });
    }
    return undefined;
  };

  return {
    login,
    logout,
    token,
    init,
    refresh,
  };
};

export { create };
